import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Input, Modal, Space, Table, Tag, Tooltip, Typography, notification } from 'antd';
import { ReloadOutlined, SendOutlined, EyeOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import ModuleNav, { MODULE_NAVS } from '../../components/ModuleNav';
import lib from '../../assets/utils/lib';
import useClaimsRollout from '../../hooks/useClaimsRollout';
import { usePermissions } from '../../assets/utils/usePermissions';

/**
 * Facility Pending Actions (spec §8) — separate from Claims Processing.
 *
 * Facilities with claims still unsubmitted from PREVIOUS months: if it is
 * October, September and older count, October does not. "Pending" is the
 * server's single definition (PendingSubmissionService): at least one
 * PA-approved item that was neither submitted nor removed by the facility.
 *
 *   View        → Encounters, filtered to that facility + pending submission.
 *   Submit All  → every eligible pending claim for that facility, straight
 *                 through the normal submission. No facility-style review.
 */

const monthLabel = (key) => {
  const [y, m] = String(key || '').split('-').map(Number);
  if (!y || !m) return key || '—';
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
};

export default function PendingSubmissionsPage() {
  const navigate = useNavigate();
  const rollout = useClaimsRollout();
  const { hasAnyPermissionWithLegacy } = usePermissions();
  const submitEnabled = Boolean(rollout.submit_separation_enabled);
  const canSubmit = submitEnabled && hasAnyPermissionWithLegacy(
    ['claims.process', 'claims.create', 'encounters.checkout', 'admin.*'],
    [1, 3],
  );

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [totals, setTotals] = useState({ facilities: 0, encounters: 0 });
  const [currentMonth, setCurrentMonth] = useState(null);
  const [submittingId, setSubmittingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await lib.getPendingSubmissions({ page, pageSize, search: search || undefined });
      const body = resp?.data || {};
      if (body.error) throw new Error(body.message || 'Failed to load');
      setRows(Array.isArray(body.data) ? body.data : []);
      setTotal(Number(body.pagination?.total || 0));
      setTotals(body.totals || { facilities: 0, encounters: 0 });
      setCurrentMonth(body.current_month || null);
    } catch (e) {
      setRows([]);
      notification.error({ message: 'Could not load pending submissions', description: e?.message });
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search]);

  useEffect(() => {
    load();
  }, [load]);

  const view = (row) => {
    navigate(`/checkins?facility_id=${row.facility_id}&status=pending`);
  };

  const submitAll = (row) => {
    Modal.confirm({
      title: `Submit all pending claims for ${row.facility_name}?`,
      content: (
        <div>
          <p>
            {row.encounters} encounter{row.encounters === 1 ? '' : 's'} from previous months will be
            submitted through the normal claim submission.
          </p>
          <p style={{ marginBottom: 0 }}>
            Each claim stays in its encounter month. Up to 200 are submitted per run.
          </p>
        </div>
      ),
      okText: 'Submit all',
      onOk: async () => {
        setSubmittingId(row.facility_id);
        try {
          const resp = await lib.submitPendingClaims({ facility_id: row.facility_id, previous_months_only: 1 });
          const parsed = lib.parseApiEnvelope(resp);
          if (!parsed.ok) {
            notification.error({ message: 'Submission failed', description: parsed.message });
            return;
          }
          const data = parsed.data || {};
          const failed = (data.results || []).filter((r) => !r.ok);
          notification[failed.length ? 'warning' : 'success']({
            message: `${data.submitted ?? 0} of ${data.total ?? 0} claim(s) submitted`,
            description: failed.length
              ? `${failed.length} could not be submitted and are still pending — open View to see why.`
              : data.truncated
                ? 'More remain — run Submit All again for the rest.'
                : `${row.facility_name} is up to date for previous months.`,
            duration: 6,
          });
          load();
        } finally {
          setSubmittingId(null);
        }
      },
    });
  };

  const columns = [
    {
      title: 'Facility',
      dataIndex: 'facility_name',
      key: 'facility_name',
      render: (v, row) => v || `Facility #${row.facility_id}`,
    },
    {
      title: 'Pending claims',
      dataIndex: 'encounters',
      key: 'encounters',
      width: 140,
      align: 'right',
      render: (v) => <Typography.Text strong>{Number(v || 0).toLocaleString()}</Typography.Text>,
    },
    {
      title: 'Outstanding months',
      key: 'months',
      width: 220,
      render: (_, row) =>
        row.oldest_month === row.latest_month ? (
          <Tag>{monthLabel(row.oldest_month)}</Tag>
        ) : (
          <Space size={4}>
            <Tag color="orange">{monthLabel(row.oldest_month)}</Tag>
            <span>→</span>
            <Tag>{monthLabel(row.latest_month)}</Tag>
          </Space>
        ),
    },
    {
      title: '',
      key: 'actions',
      width: 220,
      align: 'right',
      render: (_, row) => (
        <Space>
          <Button size="small" icon={<EyeOutlined />} onClick={() => view(row)}>
            View
          </Button>
          <Tooltip title={canSubmit ? null : submitEnabled ? 'You do not have permission to submit claims' : 'Submit All is part of the new claims flow and is not switched on yet'}>
            <Button
              size="small"
              type="primary"
              icon={<SendOutlined />}
              disabled={!canSubmit}
              loading={submittingId === row.facility_id}
              onClick={() => submitAll(row)}
            >
              Submit All
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="dashboard-page-stack">
      <header className="dash-masthead">
        <div className="dash-masthead__lead">
          <h1 className="dash-masthead__title">Facility pending actions</h1>
          <p className="dash-masthead__meta">
            Facilities with claims not yet submitted from previous months
            {currentMonth ? ` (before ${monthLabel(currentMonth)})` : ''}.
          </p>
        </div>
      </header>

      <ModuleNav items={MODULE_NAVS.claims} />

      {!submitEnabled ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Read-only until the new claims flow is switched on"
          description="You can review outstanding submissions now. Submit All becomes available when the new flow is enabled."
        />
      ) : null}

      <Space style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between' }} wrap>
        <Input.Search
          allowClear
          placeholder="Search facility"
          onSearch={(v) => {
            setPage(1);
            setSearch(v.trim());
          }}
          style={{ width: 280 }}
        />
        <Space>
          <Typography.Text type="secondary">
            {totals.facilities} facilit{totals.facilities === 1 ? 'y' : 'ies'} · {totals.encounters} pending claim
            {totals.encounters === 1 ? '' : 's'}
          </Typography.Text>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
            Refresh
          </Button>
        </Space>
      </Space>

      <Table
        rowKey="facility_id"
        size="middle"
        loading={loading}
        columns={columns}
        dataSource={rows}
        locale={{ emptyText: 'No facilities have pending claims from previous months.' }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100'],
          onChange: (p, ps) => {
            setPage(ps !== pageSize ? 1 : p);
            setPageSize(ps);
          },
        }}
      />
    </div>
  );
}
